import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Wrench, Search, Check, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Iter 150: correzione in blocco — i rapportini di un periodo (escludendo alcuni autori)
// diventano "cantiere" e le commesse -OFF perdono il suffisso. Anteprima → Applica → Annulla.
export function BulkRemoveOffCard() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [users, setUsers] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : data?.items || [])).catch(() => {});
  }, []);

  const body = () => ({ date_from: from, date_to: to, exclude_user_ids: excluded });

  const runPreview = async () => {
    if (!from || !to) return toast.error("Indica il periodo (dal / al)");
    setBusy(true); setPreview(null); setLastBatch(null);
    try {
      const { data } = await api.post("/admin/bulk/rimuovi-off/anteprima", body());
      setPreview(data.items || []);
      if (!data.count) toast.info("Nessun rapportino con -OFF nel periodo per gli autori scelti");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview?.length) return;
    if (!window.confirm(`Confermi la modifica di ${preview.length} rapportini?\n\nOgni rapportino diventa di tipo CANTIERE e le commesse -OFF perdono il suffisso. Viene salvata una copia di sicurezza: potrai annullare subito dopo.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bulk/rimuovi-off/applica", { ...body(), ids: preview.map((p) => p.id) });
      setLastBatch(data.batch_id);
      toast.success(`${data.updated} rapportini aggiornati${data.commesse_create?.length ? ` · commesse create: ${data.commesse_create.join(", ")}` : ""}`, { duration: 8000 });
      setPreview(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!lastBatch || !window.confirm("Ripristinare i rapportini com'erano prima della correzione?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bulk/annulla/${lastBatch}`);
      toast.success(`${data.restored} rapportini ripristinati`);
      setLastBatch(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const toggleUser = (id) => setExcluded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Card className="p-5 space-y-4 border-slate-300" data-testid="bulk-remove-off-card">
      <div className="font-heading font-bold tracking-tight flex items-center gap-2"><Wrench className="h-4 w-4" /> Correzione in blocco: rimuovi -OFF</div>
      <p className="text-xs text-slate-500">Scegli il periodo e gli autori da <b>escludere</b>. L'anteprima mostra cosa cambierebbe; nulla viene modificato finché non premi "Applica".</p>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div className="space-y-1"><Label className="text-xs font-bold">Dal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bulk-off-from" /></div>
        <div className="space-y-1"><Label className="text-xs font-bold">Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-off-to" /></div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs font-bold">Escludi i rapportini creati da</Label>
        <div className="flex flex-wrap gap-2">
          {users.map((u) => (
            <button key={u.id} type="button" onClick={() => toggleUser(u.id)} data-testid={`bulk-off-exclude-${u.id}`}
              className={`text-xs px-2.5 py-1.5 rounded-full border font-bold ${excluded.includes(u.id) ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              {excluded.includes(u.id) ? "✕ " : ""}{u.name || u.email}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="bulk-off-preview"><Search className="h-4 w-4 mr-1.5" /> Anteprima</Button>
        {preview?.length > 0 && (
          <Button onClick={apply} disabled={busy} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="bulk-off-apply"><Check className="h-4 w-4 mr-1.5" /> Applica a {preview.length} rapportini</Button>
        )}
        {lastBatch && (
          <Button variant="outline" onClick={undo} disabled={busy} className="border-amber-400 text-amber-800" data-testid="bulk-off-undo"><Undo2 className="h-4 w-4 mr-1.5" /> Annulla ultima correzione</Button>
        )}
      </div>
      {preview && (
        <div className="border rounded-md divide-y max-h-80 overflow-y-auto text-sm" data-testid="bulk-off-preview-list">
          {preview.length === 0 && <div className="p-3 text-slate-500">Nessun rapportino da modificare.</div>}
          {preview.map((p) => (
            <div key={p.id} className="p-3 flex flex-wrap gap-x-4 gap-y-1 items-start" data-testid={`bulk-off-row-${p.id}`}>
              <div className="font-bold w-28 shrink-0">N° {p.numero}</div>
              <div className="w-28 shrink-0 text-slate-600">{p.data_inizio === p.data_fine || !p.data_fine ? p.data : `${p.data_inizio} → ${p.data_fine}`}</div>
              <div className="w-36 shrink-0 text-slate-600 truncate">{p.created_by_name}</div>
              <div className="flex-1 min-w-[200px]">
                {p.tipo === "officina" && <span className="text-xs mr-2 px-1.5 py-0.5 rounded bg-slate-200">officina → cantiere</span>}
                {p.cambi.map((c) => <span key={c.da} className="text-xs mr-2 font-mono">{c.da} → <b>{c.a}</b></span>)}
                {p.commesse_da_creare?.length > 0 && (
                  <div className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Commessa da creare: {p.commesse_da_creare.join(", ")} (copiando cliente e descrizione dalla -OFF)</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
